import type { ISO2 } from '@/utilities/countries';

interface Market {
  id: number;
  iso2: ISO2;
}

interface Industry {
  id: number;
  name: string;
}

interface Country {
  id: number;
}

interface CurrencyInfo {
  primary_trading_item_currency_iso: string;
  primary_trading_item_currency_symbol: string;
  reporting_currency_iso: string;
  reporting_currency_symbol: string;
  reporting_unit: number;
  reporting_unit_abs: number;
  reporting_unit_text: string;
  reporting_unit_text_abs: string;
  trading_item_currency_iso: string;
  trading_item_currency_symbol: string;
}

interface Company {
  name: string;
  country: Market;
  tickerSymbol: string;
  uniqueSymbol: string;
  canonicalURL: string;
  exchangeCountryISO: ISO2;
  primaryIndustry: {
    id: number;
    name: string;
  };
  secondaryIndustry: {
    name: string;
  };
  score: {
    value: number;
    future: number;
    past: number;
    health: number;
    dividend: number;
  };
  data: {
    currencyInfo: CurrencyInfo;
  };
  analysisValue: {
    lastSharePrice: number | null;
    npvPerShare: number | null;
    marketCapUSD: number | null;
    intrinsicDiscount: number | null;
    return7d: number | null;
    return30d: number | null;
    return90d: number | null;
    return1yAbs: number | null;
    priceTarget: number | null;
    priceTargetAnalystCount: number | null;
  };
  analysisHealth: {
    netIncome: number | null;
  };
}

export interface WatchlistItem {
  companyId: string;
  createdAt: number;
  fairValue: number | null;
  company: Company;
  autoAdded: boolean | null;
}

export interface Watchlist {
  id: string;
  name: string;
  userId: string;
  createdAt: number;
  updatedAt: number;
  totalItems: number;
  itemsLimit: number;
  industries: Industry[];
  markets: Country[];
  items: WatchlistItem[];
  keys: string[];
}

/**
 * Company values mapped as required for the Watchlist.
 * Flattens nested values and formats prices & returns.
 */
export interface WatchlistCompany {
  companyId: string;
  name: string;
  createdAt: number;
  currencyISO: string;
  exchangeCountryISO: ISO2 | null;
  userFairValue: null | number;
  userFairValueFormatted: string | null;
  canonicalUrl: string;
  tickerSymbol: string;
  uniqueSymbol: string;
  marketCapUSD: number | null;
  sharePrice: number | null;
  sharePriceFormatted: string;
  intrinsicDiscount: number | null;
  intrinsicDiscountFormatted: string;
  priceTarget: number | null;
  priceTargetFormatted: string;
  priceTargetAnalystCount: number | null;
  priceTargetAnalystCountFormatted: string;
  npvPerShare: number | null;
  npvPerShareFormatted: string;
  return7D: number | null;
  return7DFormatted: string;
  return30D: number | null;
  return30DFormatted: string;
  return90D: number | null;
  return90DFormatted: string;
  return1YrAbs: number | null;
  return1YrAbsFormatted: string;
  snowflakePoints: number[];
  countryId: number | null;
  industryId: number | null;
  industryName: string;
  isProfitable: boolean;
  autoAdded: boolean | null;
}

export enum WatchlistSorting {
  RECENTLY_ADDED = '{field: createdAt direction: DESC}',
  MARKET_CAP_HIGH_TO_LOW = '{field: marketCap direction: DESC}',
  COMPANY_NAME_A_TO_Z = '{field: name direction: ASC}',
  TICKER_A_TO_Z = '{field: tickerSymbol direction: ASC}',
  PERFORMANCE_7D = '{field: return7d direction: DESC}',
  DISCOUNT_LOW_TO_HIGH = '{field: discount direction: ASC}',
  DISCOUNT_HIGH_TO_LOW = '{field: discount direction: DESC}',
}
